import { CONTENT_FORMATS, ContentFormat } from "@growth/schema";
import { Button } from "@growth/ui";
import { LoaderCircle, Share2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { useContentSettings } from "../lib/content";
import { useSocialFromPost } from "../lib/seo";
import { ErrorText } from "./page-header";

/** Social posts that point at this (published) article: one idea, the formats you tick. */
export const SocialFromArticle = ({ postId }: { postId: string }) => {
  const { atLeast } = useActive();
  const navigate = useNavigate();
  const settings = useContentSettings();
  const make = useSocialFromPost(postId);
  const brandFormats = (settings.data?.channels.flatMap((channel) => channel.formats) ??
    []) as ContentFormat[];
  const options = (
    brandFormats.length ? brandFormats : Object.keys(CONTENT_FORMATS)
  ) as ContentFormat[];
  const [chosen, setChosen] = useState<ContentFormat[]>([]);
  if (!atLeast("EDITOR")) return null;

  return (
    <div className="space-y-3 rounded-xl border border-line bg-white p-4">
      <div>
        <h2 className="text-sm font-semibold">Make social posts from it</h2>
        <p className="text-xs text-muted">Each post's tracked link points at this article.</p>
      </div>
      <div className="space-y-1">
        {options.map((format) => (
          <label key={format} className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              className="size-3.5"
              checked={chosen.includes(format)}
              onChange={(event) =>
                setChosen(
                  event.target.checked
                    ? [...chosen, format]
                    : chosen.filter((item) => item !== format),
                )
              }
            />
            {CONTENT_FORMATS[format]}
          </label>
        ))}
      </div>
      {make.isError && <ErrorText>{apiErrorMessage(make.error)}</ErrorText>}
      <Button
        className="w-full"
        disabled={!chosen.length || chosen.length > 6 || make.isPending}
        onClick={() =>
          make.mutate(chosen, {
            onSuccess: (made) => made.pieceIds[0] && navigate(`/content/${made.pieceIds[0]}`),
          })
        }
      >
        {make.isPending ? (
          <LoaderCircle className="size-4 animate-spin" />
        ) : (
          <Share2 className="size-4" />
        )}
        Make {chosen.length || ""} post{chosen.length === 1 ? "" : "s"}
      </Button>
    </div>
  );
};
